<template>
	<div class="file-detail">
		<div class="filename-download-wrapper">
			<div class="filename-download">
				<component
					:is="determinedFileIcon.component"
					:class="determinedFileIcon.class"
					halo
					type="regular"
					class="status-icon"
				/>
				<div class="filename">{{ file.file_name }}</div>
				<a download :href="filePath(file.file_path)">
					<Tippy
						arrow
						theme="large"
						:content="$t('admin_imports_file_details_download_tip')"
						:options="{ animation: 'scale' }"
					>
						<DownloadIcon type="regular" class="download-icon" />
					</Tippy>
				</a>
			</div>
			<div class="success-log">
				<Button
					v-if="file.success_log_file_path"
					link
					newTab
					:href="filePath(file.success_log_file_path)"
					>{{ $t('admin_imports_file_details_download_log') }}</Button
				>
			</div>
		</div>
		<div class="status">
			<div
				v-if="file.status !== 'processed' && file.status !== 'error'"
				class="non-error"
			>
				{{ $t(fileStatus) }}
			</div>
			<div class="error-status" v-if="!isProcessing">
				<div>
					<div v-if="file.status === 'error'">
						{{ `${$t('common_error')}: ${$t(fileError)}` }}
					</div>
				</div>
				<div v-if="file.error_log_file_path">
					<Button link :href="filePath(file.error_log_file_path)" newTab>{{
						$t('admin_imports_file_details_view_error')
					}}</Button>
				</div>
			</div>
			<div v-if="isProcessing" class="progress">
				<div class="percentage">{{ `${progressPct}%` }}</div>
				<div class="progress-bar">
					<div
						class="progress-bar-fill"
						:style="{ width: progressPct + '%' }"
					></div>
				</div>
			</div>
		</div>
		<div class="import-details" v-if="file.status !== 'not_processed'">
			<div v-if="file.total_rows > 0" class="rows-processed">
				{{
					$t('admin_imports_file_details_rows_processed', [
						file.processed_rows,
						file.total_rows,
					])
				}}
			</div>
			<div v-if="file.success_rows > 0" class="rows-success">
				<div>
					<CircleCheckIcon type="regular" />
					<div>
						{{
							$t(
								'admin_imports_file_details_rows_succeeded',
								{ Count: file.success_rows || 0 },
								file.success_rows || 0
							)
						}}
					</div>
				</div>
			</div>
			<div class="rows-failed" v-if="file.error_rows > 0">
				<div>
					<CircleExclamationIcon type="regular" />
					<div>
						{{
							$t(
								'admin_imports_file_details_rows_failed.variable',
								[file.error_rows || 0],
								file.error_rows || 0
							)
						}}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import store from '@/store'
import { Button, Tippy } from '@/components/common'
import {
	TimerIcon,
	ArrowUpFromArcIcon,
	CircleCheckIcon,
	CircleExclamationIcon,
	DownloadIcon,
} from '@/icons'

export default {
	components: {
		TimerIcon,
		ArrowUpFromArcIcon,
		CircleCheckIcon,
		CircleExclamationIcon,
		DownloadIcon,
		Button,
		Tippy,
	},
	props: ['file'],
	data() {
		return {
			store,
		}
	},
	computed: {
		isProcessing() {
			return this.file.status === 'processing'
		},

		fileIconConfig() {
			return [
				{
					condition: (f) => f.status === 'not_processed',
					component: 'TimerIcon',
					class: 'timer-icon',
				},
				{
					condition: (f) => f.status === 'processing',
					component: 'ArrowUpFromArcIcon',
					class: 'processing-icon',
				},
				{
					condition: (f) => f.status === 'processed' && f.error_rows > 0,
					component: 'CircleExclamationIcon',
					class: 'completed-errors-icon',
				},
				{
					condition: (f) => f.status === 'processed',
					component: 'CircleCheckIcon',
					class: 'processed-icon',
				},
				{
					condition: (f) => f.status === 'error',
					component: 'CircleExclamationIcon',
					class: 'failed-icon',
				},
			]
		},

		determinedFileIcon() {
			return this.fileIconConfig.find((rule) => rule.condition(this.file)) || {}
		},

		fileStatus() {
			return `admin_imports_file_status_${this.file.status}`
		},

		fileError() {
			return `admin_imports_file_error_${this.file.error_code}`
		},

		progressPct() {
			const totalRows = this.file.total_rows || 0
			const processedRows = this.file.processed_rows || 0

			const percentage = (processedRows / totalRows) * 100
			return Math.round(percentage)
		},
	},

	methods: {
		filePath(path) {
			if (path) {
				if (store.config.env === 'local') {
					let url = `https://dev.pronto.io${path}`
					if (store.auth.media_token) {
						return (url += `?t=${store.auth.media_token.accessToken}`)
					}
				} else {
					return window.location.origin + path
				}
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.file-detail {
	padding: 16px;
	border: solid 1px var(--neutral-200);
	border-radius: 8px;
	margin-bottom: 16px;
	.filename-download {
		display: flex;
		align-items: center;
	}
	.filename {
		margin: 0 8px;
		font-family: Avenir-Medium;
		font-size: 18px;
		color: var(--neutral-800);
	}
	.status {
		display: flex;
		color: var(--neutral-600);
		font-family: Avenir-Medium;
		font-size: 14px;
		margin-top: 7px;
		.non-error {
			min-width: 200px;
		}
	}
	.progress {
		display: flex;
		align-items: center;
		width: 100%;
		margin-left: 8px;
		border-left: solid 1px var(--neutral-200);
		padding-left: 8px;
	}
	.progress-bar {
		height: 6px;
		width: 100%;
		background-color: var(--neutral-200);
		border-radius: 20px;
		margin-left: 8px;
	}
	.progress-bar-fill {
		height: 100%;
		background-color: var(--blue-300);
		width: 50%;
		border-radius: 10px;
	}
	.download-icon {
		color: var(--blue-300);
		font-size: 13px;
		cursor: pointer;
	}
	.status-icon {
		font-size: 16px;
	}
	.timer-icon {
		color: var(--neutral-400);
	}
	.processing-icon {
		color: var(--blue-300);
	}
	.completed-errors-icon {
		color: var(--yellow-300);
	}
	.processed-icon {
		color: var(--teal-300);
	}
	.failed-icon {
		color: var(--red-200);
	}
	.import-details {
		border-top: solid 1px var(--neutral-200);
		margin-top: 12px;
		padding-top: 12px;
	}
	.rows-success,
	.rows-failed {
		display: flex;
		justify-content: space-between;
		font-size: 14px;

		div:first-of-type {
			display: flex;
			align-items: center;
			color: var(--neutral-600);
		}

		.pronto-icon {
			font-size: 12px;
			margin-right: 8px;
			color: var(--teal-300);
		}
	}

	.rows-failed {
		margin-top: 5px;
		.pronto-icon {
			color: var(--red-400);
		}
	}

	.rows-processed {
		color: var(--neutral-600);
		font-size: 14px;
		padding-bottom: 10px;
	}

	.filename-download-wrapper,
	.error-status {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.error-status {
		width: 100%;
		height: 20px;
		a {
			padding: 0;
			color: var(--red-400);
			&:hover {
				color: var(--red-100) !important;
			}
		}
	}

	.success-log a {
		padding: 0;
	}
}
</style>
